// frontend/src/config/roleModules.js
// Data-driven nav/module visibility, replacing the old isSupervisorPlus/
// isSuperuser two-boolean scheme in Dashboard.js. Shape intentionally
// mirrors worker/lib/auth.js's ROLE_GROUPS (module key -> allowed roles)
// so the two are easy to eyeball against each other — NOT literally shared
// code, since the frontend and Worker deploy independently.
//
// This is UX-only, same as before: hiding a nav item is a convenience, not
// a security boundary. The actual enforcement is always the backend's
// requireRole()/ROLE_GROUPS check — a role that guesses/bookmarks a hidden
// tab's hash still gets a clean 403 from every API call that tab makes.
//
// Keep in sync with worker/lib/auth.js's VALID_ROLES/ROLE_GROUPS whenever
// either changes.

export const MODULE_ROLES = {
  // Dashboard/Settings are visible to most roles — each page/section gates
  // its own content further where needed (e.g. Settings only allows
  // superuser to edit, everyone else sees read-only).
  // `waiter` and `cashier` removed 2026-09-21 (explicit user decision):
  // neither role has any use for the home dashboard's hotel-occupancy/
  // revenue stats or hotel-wide Settings — their whole job is their one
  // selling screen (Tables/POS). See Dashboard.js's DashboardContent render
  // gate and the "Dashboard"/"Settings" nav buttons, both of which now read
  // this list instead of rendering unconditionally for every role.
  DASHBOARD: [
    'hotel_admin', 'superuser', 'manager', 'supervisor', 'frontdesk',
    'chef', 'storekeeper', 'housekeeping', 'water_assistant',
  ],
  // `chef` removed 2026-09 — a Sawan-driven nav cleanup: chef's real
  // working set is Recipes/KDS/Procurement(requisitions)/Chat/Shift
  // Report, not the full Inventory screen (which they never asked for).
  INVENTORY: ['superuser', 'manager', 'supervisor', 'storekeeper'],
  SETTINGS: [
    'owner', 'hotel_admin', 'superuser', 'manager', 'supervisor', 'frontdesk',
    'chef', 'storekeeper', 'housekeeping', 'water_assistant',
  ],

  BOOKINGS: ['superuser', 'manager', 'supervisor', 'frontdesk'],
  DOCUMENTS: ['superuser', 'manager', 'supervisor', 'frontdesk'],
  // Claim packs (worker/lib/claimPacks.js) — same audience as Documents / BOOKING_MANAGEMENT.
  CLAIMS: ['superuser', 'manager', 'supervisor', 'frontdesk'],
  ROOM_MANAGEMENT: ['superuser', 'manager', 'supervisor'],
  ROOM_STATUS: ['superuser', 'manager', 'supervisor', 'housekeeping'],

  // `cashier` (new Restaurant Cashier role, 2026-09) added here — grants
  // BOTH the 'pos' (walk-up quick-sale) and 'tables' (TablesPOS) nav items,
  // since Dashboard.js builds both from this one key (a deliberate,
  // confirmed decision — the two screens aren't split apart by this key).
  POS: ['superuser', 'manager', 'supervisor', 'frontdesk', 'waiter', 'cashier'],
  POS_ADMIN: ['superuser'],
  // Recipe (ingredient list) viewing/editing — `chef` can edit
  // (POS_RECIPE_MANAGEMENT backend gate); `cashier` (2026-09) gets
  // READ-ONLY access to the same page (POS_RECIPE_READ backend gate) —
  // Recipes.js's own canEdit(role) hides Save/Add-ingredient controls for
  // cashier while the fetch/display logic stays shared.
  RECIPES: ['superuser', 'manager', 'supervisor', 'chef', 'cashier'],
  // Kitchen Display System — mirrors the backend's KDS_ACCESS group
  // exactly (worker/lib/auth.js). Whoever works a kitchen/bar screen
  // (chef, waiter-as-runner, cashier) plus supervisor+ for oversight.
  KDS: ['superuser', 'manager', 'supervisor', 'chef', 'waiter', 'cashier'],

  // Matches the backend's actual STAFF_MANAGEMENT gate (superuser-only) —
  // previously the nav showed this to supervisor/manager too, a latent
  // mismatch that 403'd on click. Fixed here as part of this rewrite.
  REPORTS: ['superuser'],
  STAFF: ['superuser'],
  USER_MANAGEMENT: ['superuser', 'hotel_admin'],
  // Role Management (2026-09-26) — mirrors the backend's SUPERUSER gate on
  // /api/v1/roles exactly (worker/index.js). Deliberately its own key
  // rather than reusing USER_MANAGEMENT's, same "narrow door" reasoning
  // the backend gate comment gives.
  ROLE_MANAGEMENT: ['superuser'],
  ACCOUNTING: ['superuser'],
  // Ledger Adjustments (2026-10-01) — deliberately NOT given a
  // MODULE_CAPABILITY entry below (unlike ACCOUNTING's FINANCIAL_MANAGEMENT
  // one), and the backend route itself gates on the real "superuser" role
  // directly, not the overridable FINANCIAL_MANAGEMENT group — see
  // worker/index.js's own comment on that route block. A junior admin role
  // created later, however it's customized via Role Management, can never
  // see or reach this: there is no override path to grant, by design.
  LEDGER_ADJUSTMENTS: ['superuser'],
  // Post the Day (worker/lib/ledgerHold.js) — review and post the takings
  // waiting in the holding area. Mirrors the backend's LEDGER_POSTING group
  // (manager-level); changing the hold settings inside it is superuser-only.
  LEDGER_POSTING: ['superuser', 'manager'],
  BUDGETS: ['superuser'],
  // Petty cash — day-to-day custody (record vouchers, top up the tin, view
  // the statement) is supervisor+. Backend gate: PETTY_CASH_MANAGEMENT.
  // Tin setup / cost-centre CRUD / closing a count with a variance is
  // PETTY_CASH_SETUP (manager+); PettyCash.js hides those controls for a
  // supervisor, and the backend 403s them anyway.
  PETTY_CASH: ['superuser', 'manager', 'supervisor'],
  // chef sees only the requisitions tab within Procurement — Procurement.js
  // itself must further gate its internal tabs per role, this only governs
  // whether the nav item appears at all.
  PROCUREMENT: ['superuser', 'manager', 'supervisor', 'chef'],

  // Water Bottling Wing — tenant-optional module. Nav visibility ALSO
  // requires the 'water_wing' entitlement (see MODULE_FEATURE below).
  // `frontdesk` added 2026-09: a deliberate, confirmed exception (see
  // worker/lib/auth.js's WATER_WING_MANAGEMENT comment) so front desk can
  // sell bottled water as part of their normal duties.
  WATER_WING: ['superuser', 'manager', 'water_assistant', 'frontdesk'],

  // Spa & Gym — tenant-optional module, mirrors WATER_WING exactly. Nav
  // visibility ALSO requires the 'spa_gym' entitlement (see MODULE_FEATURE
  // below).
  SPA_GYM: ['superuser', 'manager', 'spa_assistant'],

  // Conference & Events — NOT feature-flagged (no MODULE_FEATURE entry
  // below), unlike Water Wing/Spa & Gym: it posts to the real hotel ledger
  // the same way bookings always have, rather than being an opt-in side
  // module. Mirrors BOOKING_MANAGEMENT's frontdesk-inclusive membership
  // (worker/lib/auth.js's CONFERENCE_MANAGEMENT) — booking a function
  // space is front-of-house work, same tier as taking a guest booking.
  CONFERENCE: ['superuser', 'manager', 'supervisor', 'frontdesk'],

  // Conference meal cards (worker/lib/auth.js MEAL_CARD_SCAN): the conference desk
  // creates and prints them, the people who serve (waiter, chef) scan them.
  MEAL_CARDS: ['superuser', 'manager', 'supervisor', 'frontdesk', 'waiter', 'chef'],
  // Guest Portal (worker/lib/auth.js GUEST_PORTAL): sharing links, breakfast pre-orders, requests.
  GUEST_PORTAL: ['superuser', 'manager', 'supervisor', 'frontdesk', 'waiter', 'chef', 'cashier'],
  // Asset Register (worker/lib/auth.js ASSET_REGISTER_VIEW): what should be in each room/area.
  // Owner decision 2026-11-01: administrator and owner only for now; any other role is let in
  // through Role Management (MODULE_CAPABILITY below), and flagging / editing / costs inside the
  // page follow their own rights (ASSET_REGISTER_FLAG / _MANAGEMENT / _COSTS).
  ASSET_REGISTER: ['superuser', 'owner'],

  // In-app staff chat (migration 0020). Every valid role — mirrors the
  // backend's ROLE_GROUPS.CHAT exactly. No entitlement gate.
  CHAT: [
    'hotel_admin', 'superuser', 'manager', 'supervisor', 'frontdesk',
    'waiter', 'chef', 'storekeeper', 'housekeeping', 'water_assistant', 'cashier',
  ],
  // Self-service Z-out/X-out shift report (worker/lib/shifts.js,
  // migrations/0036_shift_sessions.sql). Mirrors the backend's
  // SHIFT_TRACKING group exactly — the four "working" roles only.
  // superuser/manager/supervisor never see this nav item.
  SHIFT_REPORT: ['frontdesk', 'waiter', 'chef', 'cashier'],
  // Corporate clients (worker/lib/corporateClients.js, migrations/
  // 0044_corporate_clients.sql) — mirrors the backend's
  // CORPORATE_CLIENT_MANAGEMENT group exactly. Manager-tier: day-to-day
  // staff bill an existing corporate account at booking time (BOOKINGS,
  // unchanged), but only a manager+ can create/edit the accounts themselves.
  CORPORATE_CLIENTS: ['superuser', 'manager'],
  // Customer Centre (worker/lib/customerCentre.js) — mirrors CUSTOMER_CENTRE.
  CUSTOMERS: ['superuser', 'manager', 'supervisor', 'frontdesk'],
  // Stock Issues (worker/lib/stockIssues.js) — mirrors the backend's
  // STOCK_ISSUE_REQUEST group, widened 2026-09-24 to every VALID_ROLE per
  // the user's explicit ask ("every logged in user should be able to make
  // requisitions... so that workload is not heavy on the storekeeper/
  // manager"). Deliberately its own nav item now, not only reachable via
  // the Inventory tab (which most of these roles have no other access to)
  // — see moduleRegistry.js's 'stock-issues' entry.
  STOCK_ISSUES: [
    'superuser', 'manager', 'supervisor', 'frontdesk', 'waiter', 'chef',
    'storekeeper', 'housekeeping', 'water_assistant', 'spa_assistant', 'cashier',
  ],
  // Portion stock (worker/lib/portionStock.js) — mirrors backend
  // POS_MANAGEMENT for viewing; editing is further gated inside
  // PortionStock.js to PORTION_STOCK_WRITE (kitchen tier + cashier).
  PORTION_STOCK: ['superuser', 'manager', 'supervisor', 'frontdesk', 'waiter', 'chef', 'cashier'],
  // Kitchen Control Sheet — mirrors backend KITCHEN_SHEET. The line-mapping
  // setup inside it is manager+ (KITCHEN_SHEET_SETUP).
  KITCHEN_SHEET: ['superuser', 'manager', 'supervisor', 'storekeeper', 'cashier'],
  // Weekly meal plan — mirrors backend MEAL_PLAN (editing is MEAL_PLAN_EDIT, which the
  // page learns from the API's can_edit).
  MEAL_PLAN: ['superuser', 'manager', 'supervisor', 'chef', 'waiter', 'cashier', 'storekeeper', 'owner'],
  // Daily Hotel Sales & Collection Report (2026-10-04) — mirrors backend
  // STAFF_MANAGEMENT exactly, same as REPORTS below: it surfaces revenue
  // figures across every department at once, a strictly narrower
  // audience than any single department's own report.
  DAILY_MANAGEMENT_REPORT: ['superuser'],
  // TALIT business reports + recipe tuning (worker/lib/talit.js) — same audience
  // and the same backend gate (FINANCIAL_REPORTS) as the Daily Report.
  TALIT_REPORTS: ['superuser'],
  // Ask TALIT, the chat (worker/lib/talitChat.js) — mirrors backend TALIA (the
  // capability keeps its first-release name). The hotel also needs the `talia`
  // entitlement (MODULE_FEATURE below) and a Claude key on the server.
  // OWNER DECISION 2026-10-12: the superuser and the owner only — no one else, whatever Role Management says.
  TALIT_CHAT: ['superuser', 'owner'],
  // Purchase Receipts (worker/lib/purchaseReceipts.js) — mirrors backend
  // PURCHASE_RECEIPTS (everything) and PURCHASE_RECEIPTS_SCAN (a manager: scan, check, confirm,
  // correct; this and last month only, no VAT figures — the server enforces it and the page
  // follows its `restricted` flag). OWNER DECISIONS 2026-10-13 / 2026-11-02; both locked on the
  // backend, so deliberately no MODULE_CAPABILITY entry.
  RECEIPTS: ['superuser', 'manager'],
  // Tax Health (worker/lib/taxHealth.js) — mirrors backend TAX_HEALTH: the eTIMS
  // check on purchases, possible savings, the staffing and salary guide (every
  // staff member's pay) and saved reports. OWNER DECISION 2026-10-18: super admin
  // only, like RECEIPTS; locked on the backend, so deliberately no MODULE_CAPABILITY entry.
  TAX_HEALTH: ['superuser'],
};

// A module that is also gated by a per-tenant ENTITLEMENT (worker/lib/
// entitlements.js). If a module key isn't here, it has no entitlement gate
// and role membership alone decides. Keep feature names in sync with
// entitlements.js's KNOWN_FEATURES.
export const MODULE_FEATURE = {
  WATER_WING: 'water_wing',
  SPA_GYM: 'spa_gym',
  PETTY_CASH: 'petty_cash',
  TALIT_CHAT: 'talia', // entitlement key kept from the first release (stored in tenant_entitlements)
  // ACCOUNTING / REPORTS map to 'advanced_reports' — on for every plan
  // today, listed so a future plan tier can withhold them.
  ACCOUNTING: 'advanced_reports',
  REPORTS: 'advanced_reports',
};

// A module that also has a genuine 1:1 backend capability
// (worker/lib/auth.js's ROLE_GROUPS) a tenant can grant to a role it
// wouldn't otherwise have, via Role Management's per-tenant overrides
// (worker/lib/roles.js). Added 2026-09-26 to close a real gap: a role
// granted a NEW capability there (e.g. frontdesk gaining
// SPA_GYM_MANAGEMENT) previously stayed invisible in the nav forever,
// because MODULE_ROLES above is a static array with no way to consult a
// tenant's overrides. Only module keys with a genuine single matching
// capability are listed — broader UX groupings with no 1:1 backend
// equivalent (DASHBOARD, SETTINGS, CHAT, POS, KDS, etc.) are deliberately
// left out and keep using MODULE_ROLES alone, since there's nothing real
// to override for them.
export const MODULE_CAPABILITY = {
  SPA_GYM: 'SPA_GYM_MANAGEMENT',
  WATER_WING: 'WATER_WING_MANAGEMENT',
  INVENTORY: 'INVENTORY_READ',
  CONFERENCE: 'CONFERENCE_MANAGEMENT',
  MEAL_CARDS: 'MEAL_CARD_SCAN',
  ASSET_REGISTER: 'ASSET_REGISTER_VIEW',
  GUEST_PORTAL: 'GUEST_PORTAL',
  PETTY_CASH: 'PETTY_CASH_MANAGEMENT',
  PROCUREMENT: 'PROCUREMENT_REQUEST',
  CORPORATE_CLIENTS: 'CORPORATE_CLIENT_MANAGEMENT',
  CUSTOMERS: 'CUSTOMER_CENTRE',
  KITCHEN_SHEET: 'KITCHEN_SHEET',
  MEAL_PLAN: 'MEAL_PLAN',
  STOCK_ISSUES: 'STOCK_ISSUE_REQUEST',
  ACCOUNTING: 'FINANCIAL_MANAGEMENT',
  LEDGER_POSTING: 'LEDGER_POSTING',
  // Added 2026-10-04 (owner role) — same nav-visibility-gap fix as
  // ROOM_STATUS below: the backend already grants these via
  // FINANCIAL_REPORTS/POS_RECIPE_READ (real, override-aware), but neither
  // module key had a MODULE_CAPABILITY entry yet, so a role granted
  // either capability couldn't see the nav item to use it.
  DAILY_MANAGEMENT_REPORT: 'FINANCIAL_REPORTS',
  TALIT_REPORTS: 'FINANCIAL_REPORTS',
  // TALIT_CHAT deliberately has NO capability entry: nobody can be granted it (the backend refuses too).
  RECIPES: 'POS_RECIPE_READ',
  // Added 2026-09-27 — the same nav-visibility gap as SPA_GYM etc: the
  // backend route (PATCH /rooms/:id/status, worker/index.js) already
  // gates on the override-aware ROOM_STATUS_WRITE via requireRole, so a
  // role granted this capability via Role Management could already call
  // the API successfully — but roleModules.js's own static ROOM_STATUS
  // array (housekeeping-only by default) never knew an override existed,
  // so a frontdesk user granted "change room status" still couldn't even
  // see the Room Status nav item to try it.
  ROOM_STATUS: 'ROOM_STATUS_WRITE',
  // Added 2026-10-09 — the same nav-visibility gap for every remaining module
  // that has a genuine 1:1 backend capability. Without these, a right granted
  // to a role or a single user (e.g. front-desk Ben given Room Management)
  // worked on the API but the menu item never appeared.
  ROOM_MANAGEMENT: 'ROOM_MANAGEMENT',
  BOOKINGS: 'BOOKING_MANAGEMENT',
  DOCUMENTS: 'BOOKING_MANAGEMENT',
  CLAIMS: 'BOOKING_MANAGEMENT',
  POS: 'POS_MANAGEMENT',
  KDS: 'KDS_ACCESS',
  CHAT: 'CHAT',
  SHIFT_REPORT: 'SHIFT_TRACKING',
  PORTION_STOCK: 'POS_MANAGEMENT',
  STAFF: 'STAFF_MANAGEMENT',
};

// role check + (if the module has one) entitlement check. `isFeatureEnabled`
// is an optional (feature) => bool — pass it from useEntitlements(); when
// omitted, only the role check applies (callers that don't have the map yet
// still work, just without entitlement filtering). `hasCapabilityOverride`
// is an optional (capabilityKey) => bool — pass it from
// useRoleCapabilities(); when the module has a MODULE_CAPABILITY entry and
// this returns true, it's OR'd with the static role check below, so a role
// granted a new per-tenant override gains visibility ON TOP OF (never
// instead of) the built-in default — additive only, and omitting this
// param entirely (existing callers) leaves behavior byte-for-byte
// unchanged.
export function canSeeModule(role, moduleKey, isFeatureEnabled, hasCapabilityOverride) {
  const allowed = MODULE_ROLES[moduleKey];
  const staticAllowed = Array.isArray(allowed) && allowed.includes(role);
  const capabilityKey = MODULE_CAPABILITY[moduleKey];
  const overrideAllowed = !!capabilityKey && typeof hasCapabilityOverride === 'function' && hasCapabilityOverride(capabilityKey);
  if (!staticAllowed && !overrideAllowed) return false;
  const feature = MODULE_FEATURE[moduleKey];
  if (feature && typeof isFeatureEnabled === 'function' && !isFeatureEnabled(feature)) {
    return false;
  }
  return true;
}
